import {rescuableError} from '../Util';
import {ItemDescription} from './ItemDescriptions';
import {ACTIVE_MARKETS, isValidMarket, marketToMarketTag} from './Markets';
import {itemFullUrl} from './Routes';
import {shopifyProductUrl, TLF_URL} from './Shopify';
import {SlackBlocks, SlackDivider, SlackImage, SlackSection, SlackText, link} from './Slack';

export type PriceRangeFilter = {
  max?: Maybe<number>;
  min?: Maybe<number>;
};
export type PageInfo = {
  endCursor: string | null;
  hasNextPage: boolean | null;
  hasPreviousPage: boolean | null;
  startCursor: string | null;
};
export type ProductFilter = {
  available?: Maybe<boolean>;
  price?: Maybe<PriceRangeFilter>;
  productType?: Maybe<string>;
  productVendor?: Maybe<string>;
  tag?: Maybe<string>;
};
export type FilterType = 'BOOLEAN' | 'LIST' | 'PRICE_RANGE';
export type FilterValue = {
  count: number;
  id: string;
  input: JSON;
  label: string;
};
export type Filter = {
  id: string;
  label: string;
  type: FilterType;
  values: Array<FilterValue>;
};
export type ShopifyImage = {
  altText?: Maybe<string>;
  height?: Maybe<number>;
  src: string;
  url: string;
  width?: Maybe<number>;
};
export type MediaImage = {
  alt?: Maybe<string>;
  id: string;
  image?: Maybe<ShopifyImage>;
  previewImage?: Maybe<ShopifyImage>;
};
export type Money = {amount: number};
export type ProductVariant = {
  availableForSale: boolean;
  barcode?: Maybe<string>;
  compareAtPrice?: Maybe<Money>;
  id: string;
  image?: Maybe<ShopifyImage>;
  price: Money;
  product: Product;
  quantityAvailable?: Maybe<number>;
  requiresShipping: boolean;
  sku?: Maybe<string>;
  title: string;
  unitPrice?: Maybe<Money>;
};
export type Product = {
  availableForSale: boolean;
  description: string;
  descriptionHtml: string;
  featuredImage?: Maybe<ShopifyImage>;
  handle: string;
  id: string;
  isGiftCard: boolean;
  media: {nodes: MediaImage[]};
  productType: string;
  publishedAt: string;
  tags: string[];
  title: string;
  totalInventory?: Maybe<number>;
  variants: {nodes: ProductVariant[]};
  vendor: string;
};
export type CartBuyerIdentityInput = {
  email?: Maybe<string>;
  phone?: Maybe<string>;
};
export type CartLineInput = {
  attributes?: Maybe<Record<string, string>[]>;
  merchandiseId: string;
  quantity?: Maybe<number>;
};
export type CartLineUpdateInput = {
  id: string;
  quantity: number;
};
export type CartInput = {
  attributes?: Maybe<Record<string, string>[]>;
  buyerIdentity?: Maybe<CartBuyerIdentityInput>;
  discountCodes?: Maybe<string[]>;
  lines?: Maybe<Array<CartLineInput>>;
  note?: Maybe<string>;
};
export type CartLine = {
  id: string;
  quantity: number;
  merchandise: {
    id: string;
    title: string;
    product: Product;
  };
};
export type Cart = {
  attributes?: {key: string; value: string}[];
  id: string;
  checkoutUrl: string;
  cost: {subtotalAmount: Money};
  lines: {nodes: CartLine[]};
};
export function getProductCharacteristics(descriptionHtml?: string) {
  if (!descriptionHtml) return [];
  const characteristics: string[][] = [];
  const tableBody = descriptionHtml.match(/<tbody>\n(.*\n)*<\/tbody>/)?.[0];
  if (!tableBody) return characteristics;
  const tableLines = tableBody.split('\n');
  let row: string[] = [];
  for (const line of tableLines) {
    if (line === '<tr>') {
      row = [];
    }
    if (line === '</tr>') {
      characteristics.push(row);
    } else {
      const cellDataRegex = /<td>(.*)<\/td>/;
      const cellData = line.match(cellDataRegex)?.[1].replace('&amp;', '&');
      if (cellData) {
        row.push(cellData);
      }
    }
  }
  return characteristics;
}
export function getDescriptionFieldValue({
  field,
  description,
}: {
  field: string;
  description: string[][];
}) {
  return description.find(element => element[0].toLowerCase() === field.toLowerCase())?.[1];
}
export function getExplicitDimensions(description: string[][]) {
  const dimension = getDescriptionFieldValue({field: 'dimensions', description});
  if (!dimension) return null;
  const dimensions = dimension.split(' x ');
  return Object.fromEntries(
    [
      ['width', dimensions.find(dimension => dimension.endsWith('ʺW'))?.slice(0, -2)],
      ['depth', dimensions.find(dimension => dimension.endsWith('ʺD'))?.slice(0, -2)],
      ['height', dimensions.find(dimension => dimension.endsWith('ʺH'))?.slice(0, -2)],
    ]
      .filter(([_, value]) => value)
      .map(([key, value]) => [key, parseFloat(value)]),
  ) as {
    width?: number;
    depth?: number;
    height?: number;
  };
}
export function publicItemToLocation(item: Product): Maybe<string> {
  const characteristic = getProductCharacteristics(item.descriptionHtml);
  return getDescriptionFieldValue({field: 'Item Location', description: characteristic});
}
export function getProductId(item: Product): Maybe<string> {
  const characteristic = getProductCharacteristics(item.descriptionHtml);
  return getDescriptionFieldValue({
    field: 'Item ID',
    description: characteristic,
  });
}
export function getItemCategory(tags: string[]) {
  return tags?.find(tag => tag.startsWith('cat'));
}

export function getItemSubCategory(tags: string[]) {
  return tags?.find(tag => tag.startsWith('sub'));
}

export function getItemSubSubCategory(tags: string[]) {
  return tags?.find(tag => tag.startsWith('ssub'));
}
export function isItemNotPriced(tags: string[]) {
  return tags.some(tag => tag === 'not-priced');
}
export function soldItemScannedSlackMessage({
  item,
  barcode,
  market,
}: {
  item: Maybe<ItemDescription>;
  barcode: number;
  market?: Maybe<string>;
}): SlackBlocks {
  const itemId = item?.id || barcode;
  const location = item?.last_location?.location || item?.item_locations?.[0]?.location;
  const imageUrl = item?.item_images?.[0]?.small_url || item?.item_images?.[0]?.big_url;
  return [
    SlackSection({text: SlackText('SOLD ITEM SCANNED IN REPERCH APP')}),
    SlackDivider(),
    SlackSection({
      text: SlackText(
        [
          `Item: ${link(itemFullUrl(itemId), `#${itemId}`)}${
            item?.posting_title ? ` — ${item.posting_title}` : ''
          }`,
          `Barcode: ${barcode}`,
          location && `Location: ${location}`,
          `Reperch: ${link(shopifyProductUrl(itemId, market || item?.market))}`,
        ]
          .filter(Boolean)
          .join('\n'),
      ),
      accessory: imageUrl ? SlackImage('item', imageUrl) : undefined,
    }),
  ];
}
export function getHumanReadableCategory(category?: string) {
  if (!category) return category;
  return category
    .replace('cat-', '')
    .split('-')
    .map(word => word[0].toUpperCase() + word.slice(1))
    .join(' ');
}
export function getHumanReadableSubCategory(subCategory?: string) {
  if (!subCategory) return subCategory;
  return subCategory
    .replace('sub-', '')
    .split('-')
    .map(word => word[0].toUpperCase() + word.slice(1))
    .join(' ');
}
export function itemToProductUrl(item: ItemDescription): string {
  const market = ACTIVE_MARKETS.find(x => x === item.market);
  if (!isValidMarket(market)) throw rescuableError('Invalid market');
  return `/${marketToMarketTag(market)}/products/${item.id}`;
}
export function getPaginationVariables({
  startCursor,
  endCursor,
  paginationSize,
}: {
  startCursor: string | null;
  endCursor: string | null;
  paginationSize: number;
}) {
  if (endCursor) {
    return {
      first: paginationSize,
      after: endCursor,
    };
  }
  if (startCursor) {
    return {
      last: paginationSize,
      before: startCursor,
    };
  }
  return {
    first: paginationSize,
  };
}
export const SEARCH_QUERY = `#graphql
  query (
    $search: String!
    $after: String
    $before: String
    $first: Int
    $last: Int
    $filters: [ProductFilter!]
  ) {
    search(
      sortKey: RELEVANCE
      query: $search
      first: $first
      last: $last
      before: $before
      after: $after
      types: PRODUCT
      productFilters: $filters
    ) {
      nodes {
        ... on Product {
          id
          title
          handle
          tags
          availableForSale
          descriptionHtml
          description
          totalInventory
          featuredImage {
            url
          }
          media(first: 10) {
            nodes {
              mediaContentType
              previewImage {
                altText
                url
              }
            }
          }
          variants (first:1) {
            nodes {
              id
              price {
                amount
              }
              compareAtPrice {
                amount
              }
            }
          }
        }
      }
      pageInfo {
        startCursor
        endCursor
        hasNextPage
        hasPreviousPage
      }
      totalCount
    }
  }
`;

export const COLLECTION_QUERY = `#graphql
  query CollectionDetails(
    $handle: String!,
    $filters: [ProductFilter!],
    $sortKey: ProductCollectionSortKeys,
    $reverse: Boolean,
    $first: Int,
    $last: Int,
    $after: String,
    $before: String
  ) {
    collection(handle: $handle) {
      title
      handle
      seo {
        title
        description
      }
      products (
        first: $first,
        last: $last,
        after: $after,
        before: $before,
        filters: $filters,
        sortKey: $sortKey,
        reverse: $reverse
      ) {
        filters {
          id
          values {
            id
            count
            label
          }
        }
        nodes {
          id
          title
          handle
          tags
          availableForSale
          descriptionHtml
          description
          totalInventory
          featuredImage {
            url
          }
          media(first: 10) {
            nodes {
              mediaContentType
              previewImage {
                altText
                url
              }
            }
          }
          variants (first:1) {
            nodes {
              id
              price {
                amount
              }
              compareAtPrice {
                amount
              }
              barcode
            }
          }
        }
        pageInfo {
          endCursor
          hasNextPage
          hasPreviousPage
          startCursor
        }
      }
    }
  }
`;

export const PRODUCT_QUERY = `#graphql
query Product($handle: String!) {
  product(handle: $handle) {
    id
    title
    vendor
    handle
    description
    descriptionHtml
    productType
    tags
    availableForSale
    totalInventory
    environmentalImpact: metafield (key: "environmentalImpact", namespace: "remoov") {
      value
    }
    extendedTags: metafield (key: "ai_predicted_tags", namespace: "remoov") {
      value
    }
    originalPrice: metafield (key: "originalPrice", namespace: "remoov") {
      value
    }
    featuredImage {
      url
    }
    seo {
      description
      title
    }
    media(first: 10) {
      nodes {
        mediaContentType
        previewImage {
          altText
          url
        }
      }
    }
    variants(first: 1) {
      nodes {
        id
        compareAtPrice {
          amount
        }
        price {
          amount
        }
      }
    }
  }
}
`;
export const RECOMMENDED_PRODUCTS_QUERY = `#graphql
  query ProductRecommendations($query: String!,  $after: String,
    $before: String){
    products(first: 20, query: $query, sortKey: CREATED_AT, reverse: true, after: $after, before: $before) {
      nodes {
        id
        handle
        title
        description
        descriptionHtml
        productType
        tags
        availableForSale
        variants(first: 1) {
          nodes {
            id
            compareAtPrice {
              amount
            }
            price {
              amount
            }
            originalPrice: metafield (key: "originalPrice", namespace: "remoov") {
              value
            }
          }
        }
        featuredImage {
          url
        }
        media(first: 10) {
        nodes {
          mediaContentType
          previewImage {
            altText
            url
          }
        }
    }
      }
      pageInfo {
        startCursor
        endCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`;
const LINES_CART_FRAGMENT = `
  #graphql
  fragment CartLinesFragment on Cart {
    id
    totalQuantity
  }
`;
const USER_ERROR_FRAGMENT = `
  #graphql
  fragment ErrorFragment on CartUserError {
    message
    field
    code
  }
`;
const MUTATION_CART_FRAGMENT = `
  #graphql
  fragment CartMutationFragment on Cart {
    id
      checkoutUrl
      cost{
        subtotalAmount{
          amount
        }
      }
      attributes {
        key
        value
      }
      lines(first: 250) {
          nodes {
            id
            quantity
            merchandise {
              ... on ProductVariant {
                id
                title
                product{
                  id
                  handle
                  tags
                  title
                  availableForSale
                  descriptionHtml
                  description
                  totalInventory
                  featuredImage {
                    url
                  }
                  media(first: 10) {
                    nodes {
                      mediaContentType
                      previewImage {
                        altText
                        url
                      }
                    }
                  }
                  variants (first:1) {
                    nodes {
                      id
                      price {
                        amount
                      }
                      compareAtPrice {
                        amount
                      }
                      barcode
                    }
                  }
                }
            }
          }
      }
    }
  }
`;
export const CART_CREATE_QUERY = `#graphql
mutation ($input: CartInput!, $country: CountryCode = ZZ, $language: LanguageCode)
@inContext(country: $country, language: $language) {
  cartCreate(input: $input) {
    cart {
      ...CartLinesFragment
    }
    errors: userErrors {
      ...ErrorFragment
    }
  }
}
  ${LINES_CART_FRAGMENT}
  ${USER_ERROR_FRAGMENT}
`;
export const GET_CART_QUERY = `#graphql
query getCart($cartId: ID!) {
    cart(id: $cartId) {
      ...CartMutationFragment
  }
}
  ${MUTATION_CART_FRAGMENT}
`;
export const ADD_TO_CART_QUERY = `#graphql
mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
  cartLinesAdd(cartId: $cartId, lines: $lines) {
    cart {
      ...CartMutationFragment
  }
  }
  }
  ${MUTATION_CART_FRAGMENT}
`;
export const REMOVE_FROM_CART_QUERY = `#graphql
mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
  cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
    cart {
    ...CartMutationFragment
    }
  }
}
${MUTATION_CART_FRAGMENT}
`;
export const UPDATE_CART_QUERY = `#graphql
mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
  cartLinesUpdate(cartId: $cartId, lines: $lines) {
    cart {
      ...CartMutationFragment
    }
  }
}
${MUTATION_CART_FRAGMENT}
`;

export function getReperchPostCheckoutScheduleUrl(
  orderId: string,
  fulfillmentType: string,
): string {
  return fulfillmentType === 'Delivery'
    ? `${TLF_URL}/post-checkout/${orderId}/delivery/calendar`
    : `${TLF_URL}/post-checkout/${orderId}/pickup`;
}

export const UPDATE_CART_ATTRIBUTES_QUERY = `#graphql
mutation cartAttributesUpdate($cartId: ID!, $attributes: [AttributeInput!]!) {
  cartAttributesUpdate(cartId: $cartId, attributes: $attributes) {
    cart {
      ...CartMutationFragment
    }
  }
}
${MUTATION_CART_FRAGMENT}
`;
export function twitterShareUrl(title: string, url?: string) {
  return url
    ? `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${url}`
    : `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}`;
}

export function facebookShareUrl(url: string) {
  return `https://www.facebook.com/sharer/sharer.php?u=${url}`;
}

export function pinterestShareUrl(title: string, url: string, imageUrl?: string) {
  return `http://pinterest.com/pin/create/button/?url=${url}&description=${title}${
    imageUrl ? `&media=${imageUrl}` : ''
  }`;
}
